import React, { useState } from 'react';

import Button from '@sats-group/ui-lib/react/button/button';
import LinkButton from '@sats-group/ui-lib/react/link-button/link-button';
import Message from '@sats-group/ui-lib/react/message/message';
import Text from '@sats-group/ui-lib/react/text/text';

import CampaignBanner from 'shared/components/campaign-banner/campaign-banner';
import ContentContainer from 'shared/components/content-container/content-container';
import Discountcode from 'shared/components/discount-code/discount-code';
import { useMessage } from 'shared/hooks/use-message';

import { registerFormId } from 'ui/pages/register/register.page';

import CheckoutSummary from '../checkout-summary/checkout-summary';
import LayoutHeader from '../layout-header/layout-header';

import type { SplitLayout as Props } from './split-layout.props';

const SplitLayout: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  campaignBanner,
  campaignCode,
  campaignDisclaimer,
  checkoutSummary,
  children,
  corporateCode,
  header,
  language,
  messages = [],
  title,
  button,
  linkButton,
  standardPrice,
  totalPrice,
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { messagesQueue, getAction } = useMessage(messages, language);

  const handleSubmit = () => {
    setIsSubmitting(true);
    const form = document.getElementById(registerFormId) as HTMLFormElement;
    if (form) {
      if (form.checkValidity()) {
        form.submit();
      } else {
        form.reportValidity();
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="split-layout">
      <div className="split-layout__header">
        <LayoutHeader {...header} />
      </div>
      <ContentContainer theme={ContentContainer.themes.wide}>
        <div className="split-layout__main">
          {campaignBanner ? (
            <div className="split-layout__campaign-banner">
              <CampaignBanner {...campaignBanner} />
            </div>
          ) : null}
          <div className="split-layout__left-outer">
            <div className="split-layout__left-inner">
              <div className="split-layout__title">
                <Text
                  elementName="h1"
                  size={Text.sizes.headline2}
                  theme={Text.themes.headline}
                  italic
                >
                  {title}
                </Text>
              </div>
              {messagesQueue.length ? (
                <div className="split-layout__messages">
                  {messagesQueue.map(message => (
                    <Message
                      key={message.text}
                      inline
                      {...message}
                      action={getAction(message)}
                    />
                  ))}
                </div>
              ) : null}
              {children}
            </div>
          </div>
          <div className="split-layout__right-outer">
            <div className="split-layout__right-inner">
              {checkoutSummary ? (
                <div className="split-layout__summary">
                  <CheckoutSummary {...checkoutSummary} />
                </div>
              ) : null}
              {campaignCode || corporateCode ? (
                <div className="split-layout__discounts">
                  {campaignCode ? <Discountcode {...campaignCode} /> : null}
                  {corporateCode ? <Discountcode {...corporateCode} /> : null}
                </div>
              ) : null}
              {campaignDisclaimer ? (
                <div className="split-layout__campaign-disclaimer">
                  <Text
                    theme={Text.themes.emphasis}
                    size={Text.sizes.interface}
                  >
                    {campaignDisclaimer.campaign}
                  </Text>
                  <Text size={Text.sizes.interface}>
                    {campaignDisclaimer.disclaimer}
                  </Text>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </ContentContainer>
      {(button || linkButton) && totalPrice ? (
        <footer className="split-layout__footer">
          <ContentContainer
            className="split-layout__footer-content"
            theme={ContentContainer.themes.wide}
          >
            <div className="split-layout__footer-prices">
              {standardPrice ? (
                <div className="split-layout__standard-price">
                  <Text size={Text.sizes.interface}>{standardPrice.title}</Text>
                  <Text size={Text.sizes.interface}>{standardPrice.value}</Text>
                </div>
              ) : null}
              <div className="split-layout__total-price">
                <Text size={Text.sizes.large}>{totalPrice.title}</Text>
                <Text size={Text.sizes.large} theme={Text.themes.emphasis}>
                  {totalPrice.value}
                </Text>
              </div>
            </div>
            {button ? (
              <Button
                {...button}
                data-test-next-footer
                disabled={isSubmitting}
                onClick={handleSubmit}
                spinning={isSubmitting}
                wide
              />
            ) : null}
            {linkButton ? (
              <LinkButton {...linkButton} data-test-next-footer wide />
            ) : null}
          </ContentContainer>
        </footer>
      ) : null}
    </div>
  );
};

export default SplitLayout;
